import React, { useMemo } from 'react';
import classNames from 'classnames';
import Headset from '@mui/icons-material/Headset';
import Image from '@mui/icons-material/Image';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import YouTube from '@mui/icons-material/YouTube';
import { SvgIcon } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import {
  AUDIO_MIMES,
  EXCEL_MIMES,
  POWER_POINT_MIMES,
  WORD_MIMES,
  PDF_MIMES,
  IMAGE_CONTENT_MIMES,
  VIDEO_MIMES,
} from '../../config/variables';
import { ReactComponent as Word } from '../../svg/icons/document.svg';
import { ReactComponent as PDF } from '../../svg/icons/pdf.svg';
import { ReactComponent as Slides } from '../../svg/icons/slides.svg';
import { ReactComponent as SpreadSheet } from '../../svg/icons/spreadsheet.svg';

const useStyles = makeStyles((theme: Theme) => ({
  disabled: {
    color: theme.palette.text.disabled,
  },
  iconAudio: { color: 'var(--warning-color)' },
  iconDocument: { color: 'var(--secondary-color)' },
  iconImage: { color: 'var(--file-type-image)' },
  iconVideo: { color: 'var(--error-color)' },
}));

interface Props {
  disabled?: boolean;
  mimeType: string;
  size?: 'medium' | 'small';
}

const FileIcon: React.FC<Props> = (props: Props) => {
  const classes = useStyles();
  const theme = useTheme();

  const { disabled = false, mimeType, size = 'small' } = props;

  const customIconSize = useMemo(
    () => (size === 'small' ? theme.spacing(2.5) : theme.spacing(3)),
    [size, theme]
  );

  let icon = (
    <InsertDriveFileOutlined
      className={classNames({ [classes.disabled]: disabled })}
      fontSize={size}
    />
  );

  if (AUDIO_MIMES.includes(mimeType)) {
    icon = (
      <Headset
        className={classNames(classes.iconAudio, {
          [classes.disabled]: disabled,
        })}
        fontSize={size}
      />
    );
  }

  if (EXCEL_MIMES.includes(mimeType)) {
    icon = (
      <SvgIcon>
        <SpreadSheet
          className={classNames(classes.iconDocument, {
            [classes.disabled]: disabled,
          })}
          height={customIconSize}
          width={customIconSize}
        />
      </SvgIcon>
    );
  }

  if (IMAGE_CONTENT_MIMES.includes(mimeType)) {
    icon = (
      <Image
        className={classNames(classes.iconImage, {
          [classes.disabled]: disabled,
        })}
        fontSize={size}
      />
    );
  }

  if (PDF_MIMES.includes(mimeType)) {
    icon = (
      <SvgIcon>
        <PDF
          className={classNames(classes.iconDocument, {
            [classes.disabled]: disabled,
          })}
          height={customIconSize}
          width={customIconSize}
        />
      </SvgIcon>
    );
  }

  if (POWER_POINT_MIMES.includes(mimeType)) {
    icon = (
      <SvgIcon>
        <Slides
          className={classNames(classes.iconDocument, {
            [classes.disabled]: disabled,
          })}
          height={customIconSize}
          width={customIconSize}
        />
      </SvgIcon>
    );
  }

  if (VIDEO_MIMES.includes(mimeType)) {
    icon = (
      <YouTube
        className={classNames(classes.iconVideo, {
          [classes.disabled]: disabled,
        })}
        fontSize={size}
      />
    );
  }

  if (WORD_MIMES.includes(mimeType)) {
    icon = (
      <SvgIcon>
        <Word
          className={classNames(classes.iconDocument, {
            [classes.disabled]: disabled,
          })}
          height={customIconSize}
          width={customIconSize}
        />
      </SvgIcon>
    );
  }

  return icon;
};

export default FileIcon;
